/**
 * useExport.js
 * Hook for exporting report data to CSV.
 * Fetches all rows (limit=500) matching current filters, then triggers download.
 */

import { useState, useCallback } from 'react';
import { exportToCsv } from '../utils/exportCsv';

/**
 * @param {Function} apiFn - API fetch function (e.g. api.getSales)
 * @param {object} params - current filter/sort params (page and limit will be overridden)
 * @param {Array<{ key: string, label: string }>} columns - CSV column definitions
 * @param {string} filename - download filename (e.g. 'sales-velocity-30d-2024-01-15.csv')
 * @returns {{ exporting: boolean, handleExport: Function }}
 */
export function useExport(apiFn, params, columns, filename) {
  const [exporting, setExporting] = useState(false);

  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      const result = await apiFn({ ...params, page: 1, limit: 500 });
      const rows = result?.data || [];
      exportToCsv(filename, rows, columns);
    } finally {
      setExporting(false);
    }
  }, [apiFn, params, columns, filename]);

  return { exporting, handleExport };
}
